import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

export default function PayPalPayment({ order, onPaid, onMockPay, createPath, capturePath }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/config/paypal").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false })); }, []);

  const _create = createPath || `/orders/${order?.id}/paypal/create-order`;
  const _capture = capturePath || `/orders/${order?.id}/paypal/capture`;

  if (!cfg) return <div className="text-slate-500 text-sm">Caricamento configurazione pagamento…</div>;

  if (!cfg.enabled || !cfg.client_id) {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            PayPal non ancora configurato. Imposta <code className="font-mono">PAYPAL_CLIENT_ID</code> e <code className="font-mono">PAYPAL_SECRET</code> nelle Env Secrets del backend per abilitare i pagamenti reali.
            <br/>Puoi comunque simulare il pagamento in modalità demo.
          </div>
        </div>
        {onMockPay && (
          <Button onClick={onMockPay} className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-white" data-testid="paypal-mock-pay">
            Simula pagamento (demo)
          </Button>
        )}
      </div>
    );
  }

  return (
    <div data-testid="paypal-buttons-container">
      <div className="text-xs text-slate-500 mb-3">
        Modalità: <span className="font-semibold uppercase">{cfg.mode}</span> · Valuta: {cfg.currency}
      </div>
      <PayPalScriptProvider options={{
        "client-id": cfg.client_id,
        currency: cfg.currency || "EUR",
        intent: "capture",
        components: "buttons",
      }}>
        <PayPalButtons
          style={{ layout: "vertical", shape: "pill", label: "paypal" }}
          createOrder={async () => {
            try {
              const { data } = await api.post(_create);
              return data.paypal_order_id;
            } catch (e) {
              toast.error("Errore creazione ordine PayPal: " + (e.response?.data?.detail || e.message));
              throw e;
            }
          }}
          onApprove={async (data) => {
            try {
              const { data: result } = await api.post(_capture, { paypal_order_id: data.orderID });
              const okStatus = result.status === "COMPLETED" || result.payment_status === "pagato" || result.ok;
              if (okStatus) {
                toast.success(`Pagamento completato${result.transaction_id ? ' · Transazione ' + result.transaction_id : ''}`);
                onPaid?.(data.orderID);
              } else {
                toast.error("Pagamento in stato: " + (result.status || result.payment_status));
              }
            } catch (e) {
              toast.error("Errore conferma pagamento: " + (e.response?.data?.detail || e.message));
            }
          }}
          onCancel={() => toast.info("Pagamento annullato dall'utente")}
          onError={(err) => toast.error("Errore PayPal: " + (err?.message || "sconosciuto"))}
        />
      </PayPalScriptProvider>
    </div>
  );
}
